import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import Layout from '../../components/Layout';

const API = '/api/v1/fsm';

const FSMDashboard = () => {
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [woStats, setWoStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStats = useCallback(async () => {
    try {
      const [dashRes, woRes] = await Promise.all([
        fetch(API + '/dashboard-stats', { headers }),
        fetch(API + '/work-orders/stats', { headers })
      ]);
      const dashJson = await dashRes.json();
      const woJson = await woRes.json();
      if (dashJson.success) setData(dashJson.data);
      if (woJson.success) setWoStats(woJson.data);
    } catch (e) { console.error('Dashboard fetch error:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Auto refresh every 60s
  useEffect(() => {
    const iv = setInterval(fetchStats, 60000);
    return () => clearInterval(iv);
  }, [fetchStats]);

  const fmtNum = (n) => (n || 0).toLocaleString();
  const fmtMoney = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString(isRTL ? 'ar-EG' : 'en-GB', { day: 'numeric', month: 'short' });
  };
  const daysUntil = (d) => {
    if (!d) return 0;
    return Math.ceil((new Date(d) - new Date()) / 86400000);
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
          {isRTL ? 'جاري التحميل...' : 'Loading dashboard...'}
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>
          {isRTL ? 'فشل تحميل البيانات' : 'Failed to load dashboard data'}
        </div>
      </Layout>
    );
  }

  const eq = data.equipment || {};
  const ct = data.contracts || {};
  const cl = data.clients || {};
  const mp = data.maintenance || {};

  // Stat card component
  const StatCard = ({ icon, value, label, sub, color, onClick }) => (
    <div onClick={onClick} style={{
      background: '#111827', borderRadius: 14, padding: '18px 20px',
      border: '1px solid #1f2937', cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s', flex: 1, minWidth: 140,
    }}
    onMouseEnter={e => { if (onClick) e.currentTarget.style.borderColor = color || '#3b82f6'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = '#1f2937'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 28, fontWeight: 700, color: color || '#f9fafb', lineHeight: 1.1 }}>
            {value}
          </div>
          <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 4, fontWeight: 500 }}>{label}</div>
          {sub && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{sub}</div>}
        </div>
        <span style={{ fontSize: 28, opacity: 0.6 }}>{icon}</span>
      </div>
    </div>
  );

  // Alert card component
  const AlertCard = ({ icon, count, label, color, onClick }) => (
    count > 0 ? (
      <div onClick={onClick} style={{
        display: 'flex', alignItems: 'center', gap: 12,
        background: `${color}15`, borderRadius: 10, padding: '12px 16px',
        border: `1px solid ${color}30`, cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
      }}>
        <span style={{ fontSize: 20 }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, color: '#d1d5db', fontWeight: 500 }}>{label}</div>
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color }}>{count}</div>
      </div>
    ) : null
  );

  return (
    <Layout>
      <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: 24, fontFamily: 'inherit' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#f9fafb' }}>
              🔧 {isRTL ? 'لوحة تحكم الصيانة' : 'Field Service Dashboard'}
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              {isRTL ? 'نظرة عامة في الوقت الفعلي' : 'Real-time overview'}
            </p>
          </div>
          <button onClick={fetchStats} style={{
            padding: '8px 20px', borderRadius: 8, border: '1px solid #374151',
            background: '#111827', color: '#d1d5db', cursor: 'pointer', fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            🔄 {isRTL ? 'تحديث' : 'Refresh'}
          </button>
        </div>

        {/* KPI Cards Row */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          <StatCard icon="🔧" value={fmtNum(eq.total)} label={isRTL ? 'المعدات' : 'Equipment'}
            sub={`${fmtNum(eq.active)} ${isRTL ? 'نشط' : 'active'}`}
            color="#3b82f6" onClick={() => navigate('/fsm/equipment')} />
          <StatCard icon="📄" value={fmtNum(ct.total)} label={isRTL ? 'العقود' : 'Contracts'}
            sub={`${fmtNum(ct.active)} ${isRTL ? 'ساري' : 'active'}`}
            color="#10b981" onClick={() => navigate('/fsm/contracts')} />
          <StatCard icon="🏭" value={fmtNum(cl.total)} label={isRTL ? 'العملاء' : 'Clients'}
            color="#8b5cf6" onClick={() => navigate('/fsm/clients')} />
          <StatCard icon="🛠️" value={fmtNum(mp.total)} label={isRTL ? 'خطط الصيانة' : 'Maintenance Plans'}
            sub={`${fmtNum(mp.active)} ${isRTL ? 'نشط' : 'active'}`}
            color="#f59e0b" onClick={() => navigate('/fsm/maintenance-plans')} />
          <StatCard icon="💰" value={`${fmtMoney(ct.total_active_value)}`}
            label={isRTL ? 'قيمة العقود' : 'Active Contract Value'}
            color="#10b981" />
        </div>

        {/* Alerts Row */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <AlertCard icon="⚠️" count={ct.expiring_7d || 0} color="#f59e0b"
              label={isRTL ? 'عقود تنتهي خلال 7 أيام' : 'Contracts expiring in 7 days'}
              onClick={() => navigate('/fsm/contracts')} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <AlertCard icon="🔴" count={ct.expiring_30d || 0} color="#ef4444"
              label={isRTL ? 'عقود تنتهي خلال 30 يوم' : 'Contracts expiring in 30 days'}
              onClick={() => navigate('/fsm/contracts')} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <AlertCard icon="🔧" count={mp.overdue || 0} color="#ef4444"
              label={isRTL ? 'خطط صيانة متأخرة' : 'Overdue maintenance plans'}
              onClick={() => navigate('/fsm/maintenance-plans')} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <AlertCard icon="🛡️" count={eq.warranty_expired || 0} color="#6b7280"
              label={isRTL ? 'ضمانات منتهية' : 'Expired warranties'} />
          </div>
        </div>

        {/* Two Column Layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

          {/* Expiring Contracts */}
          <div style={{ background: '#111827', borderRadius: 14, padding: 20, border: '1px solid #1f2937' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 600, color: '#f9fafb', display: 'flex', alignItems: 'center', gap: 8 }}>
              ⏰ {isRTL ? 'عقود تنتهي قريباً' : 'Expiring Contracts'}
            </h3>
            {(data.expiring_contracts || []).length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.expiring_contracts.map(c => {
                  const days = daysUntil(c.end_date);
                  const urgencyColor = days <= 7 ? '#ef4444' : days <= 14 ? '#f59e0b' : '#6b7280';
                  return (
                    <div key={c.id} onClick={() => navigate(`/fsm/contracts/${c.id}`)} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 12px', borderRadius: 8, background: '#0d1117',
                      border: '1px solid #1f2937', cursor: 'pointer', transition: 'border-color 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#374151'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#1f2937'}
                    >
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#e0e0e0' }}>{c.contract_number}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{c.client_name} — {c.title}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: urgencyColor }}>
                          {days} {isRTL ? 'يوم' : 'd'}
                        </div>
                        <div style={{ fontSize: 10, color: '#6b7280' }}>{fmtDate(c.end_date)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: 20, textAlign: 'center', color: '#374151', fontSize: 13 }}>
                ✅ {isRTL ? 'لا توجد عقود تنتهي قريباً' : 'No contracts expiring soon'}
              </div>
            )}
          </div>

          {/* Overdue Maintenance Plans */}
          <div style={{ background: '#111827', borderRadius: 14, padding: 20, border: '1px solid #1f2937' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 600, color: '#f9fafb', display: 'flex', alignItems: 'center', gap: 8 }}>
              🔧 {isRTL ? 'صيانة متأخرة' : 'Overdue Maintenance'}
            </h3>
            {(data.overdue_plans || []).length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.overdue_plans.map(p => {
                  const days = Math.abs(daysUntil(p.next_due_date));
                  return (
                    <div key={p.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 12px', borderRadius: 8, background: '#0d1117',
                      border: '1px solid #7f1d1d40',
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#fca5a5' }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{p.contract_number || '—'} · {p.frequency}</div>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>
                        {days}{isRTL ? ' يوم تأخير' : 'd overdue'}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: 20, textAlign: 'center', color: '#374151', fontSize: 13 }}>
                ✅ {isRTL ? 'لا توجد صيانة متأخرة' : 'All maintenance on schedule'}
              </div>
            )}
          </div>
        </div>

        {/* Bottom Row: Equipment by Category + Recent Equipment + Contract Value */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>

          {/* Equipment by Category */}
          <div style={{ background: '#111827', borderRadius: 14, padding: 20, border: '1px solid #1f2937' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 600, color: '#f9fafb' }}>
              📊 {isRTL ? 'المعدات حسب الفئة' : 'Equipment by Category'}
            </h3>
            {(data.equipment_by_category || []).length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.equipment_by_category.map((cat, i) => {
                  const maxCount = Math.max(...data.equipment_by_category.map(c => c.count));
                  const pct = maxCount > 0 ? (cat.count / maxCount) * 100 : 0;
                  return (
                    <div key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                        <span style={{ color: '#d1d5db' }}>{cat.icon || '📦'} {cat.name}</span>
                        <span style={{ color: '#6b7280', fontWeight: 600 }}>{cat.count}</span>
                      </div>
                      <div style={{ height: 6, borderRadius: 3, background: '#1f2937' }}>
                        <div style={{ height: '100%', borderRadius: 3, background: '#3b82f6', width: `${pct}%`, transition: 'width 0.5s' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: 16, textAlign: 'center', color: '#374151', fontSize: 13 }}>
                {isRTL ? 'لا توجد بيانات' : 'No data'}
              </div>
            )}
          </div>

          {/* Recent Equipment */}
          <div style={{ background: '#111827', borderRadius: 14, padding: 20, border: '1px solid #1f2937' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 600, color: '#f9fafb' }}>
              🆕 {isRTL ? 'أحدث المعدات' : 'Recent Equipment'}
            </h3>
            {(data.recent_equipment || []).length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.recent_equipment.map(e => (
                  <div key={e.id} onClick={() => navigate(`/fsm/equipment/${e.id}`)} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                    background: '#0d1117', border: '1px solid #1f2937',
                    transition: 'border-color 0.2s',
                  }}
                  onMouseEnter={ev => ev.currentTarget.style.borderColor = '#374151'}
                  onMouseLeave={ev => ev.currentTarget.style.borderColor = '#1f2937'}
                  >
                    <span style={{ fontSize: 20 }}>{e.category_icon || '⚙️'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#e0e0e0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{e.client_name} · {e.category_name}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: 16, textAlign: 'center', color: '#374151', fontSize: 13 }}>
                {isRTL ? 'لا توجد بيانات' : 'No data'}
              </div>
            )}
          </div>

          {/* Contract Value by Type */}
          <div style={{ background: '#111827', borderRadius: 14, padding: 20, border: '1px solid #1f2937' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 600, color: '#f9fafb' }}>
              💰 {isRTL ? 'قيمة العقود حسب النوع' : 'Contract Value by Type'}
            </h3>
            {(data.contract_value_by_type || []).length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {data.contract_value_by_type.map((t, i) => {
                  const typeLabels = { maintenance: isRTL ? 'صيانة' : 'Maintenance', warranty: isRTL ? 'ضمان' : 'Warranty', on_demand: isRTL ? 'عند الطلب' : 'On Demand', comprehensive: isRTL ? 'شامل' : 'Comprehensive' };
                  const colors = { maintenance: '#3b82f6', warranty: '#10b981', on_demand: '#f59e0b', comprehensive: '#8b5cf6' };
                  return (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 12px', borderRadius: 8, background: '#0d1117',
                      borderLeft: `3px solid ${colors[t.contract_type] || '#6b7280'}`,
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#e0e0e0' }}>
                          {typeLabels[t.contract_type] || t.contract_type}
                        </div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{t.count} {isRTL ? 'عقد' : 'contracts'}</div>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: colors[t.contract_type] || '#f9fafb' }}>
                        {fmtMoney(t.total_value)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: 16, textAlign: 'center', color: '#374151', fontSize: 13 }}>
                {isRTL ? 'لا توجد بيانات' : 'No data'}
              </div>
            )}
          </div>
        </div>

        {/* Equipment Status Bar */}
        {eq.total > 0 && (
          <div style={{ background: '#111827', borderRadius: 14, padding: 20, border: '1px solid #1f2937', marginTop: 16 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: '#f9fafb' }}>
              📈 {isRTL ? 'حالة المعدات' : 'Equipment Status'}
            </h3>
            <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', background: '#1f2937' }}>
              {[
                { key: 'active', color: '#10b981', count: eq.active },
                { key: 'maintenance', color: '#f59e0b', count: eq.in_maintenance },
                { key: 'inactive', color: '#ef4444', count: eq.inactive },
                { key: 'retired', color: '#6b7280', count: eq.retired },
              ].filter(s => s.count > 0).map(s => (
                <div key={s.key} style={{
                  width: `${(s.count / eq.total) * 100}%`,
                  background: s.color,
                  transition: 'width 0.5s',
                }} title={`${s.key}: ${s.count}`} />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
              {[
                { label: isRTL ? 'نشط' : 'Active', color: '#10b981', count: eq.active },
                { label: isRTL ? 'صيانة' : 'Maintenance', color: '#f59e0b', count: eq.in_maintenance },
                { label: isRTL ? 'متوقف' : 'Inactive', color: '#ef4444', count: eq.inactive },
                { label: isRTL ? 'متقاعد' : 'Retired', color: '#6b7280', count: eq.retired },
              ].filter(s => s.count > 0).map(s => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />
                  <span style={{ color: '#9ca3af' }}>{s.label}: <strong style={{ color: '#e0e0e0' }}>{s.count}</strong></span>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* Work Orders Section */}
        {woStats && (
          <div style={{ marginTop: 24 }}>
            <h2 style={{ color: '#f9fafb', fontSize: 18, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              🔧 {isRTL ? 'أوامر العمل' : 'Work Orders'}
            </h2>

            {/* WO KPI Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 20 }}>
              <div onClick={() => navigate('/fsm/work-orders')} style={{ backgroundColor: 'rgba(26,32,56,0.8)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', padding: 16, cursor: 'pointer', borderLeft: '3px solid #3b82f6' }}>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>{isRTL ? 'مفتوح' : 'Open'}</div>
                <div style={{ color: '#f9fafb', fontSize: 28, fontWeight: 700 }}>{woStats.open || 0}</div>
              </div>
              <div onClick={() => navigate('/fsm/work-orders?status=in_progress,assigned,open')} style={{ backgroundColor: 'rgba(26,32,56,0.8)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', padding: 16, cursor: 'pointer', borderLeft: '3px solid #ef4444' }}>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>{isRTL ? 'متأخر' : 'Overdue'}</div>
                <div style={{ color: '#ef4444', fontSize: 28, fontWeight: 700 }}>{woStats.overdue || 0}</div>
              </div>
              <div style={{ backgroundColor: 'rgba(26,32,56,0.8)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', padding: 16, borderLeft: '3px solid #10b981' }}>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>{isRTL ? 'مكتمل هذا الشهر' : 'Completed (Month)'}</div>
                <div style={{ color: '#f9fafb', fontSize: 28, fontWeight: 700 }}>{woStats.completed_this_month || 0}</div>
              </div>
              <div style={{ backgroundColor: 'rgba(26,32,56,0.8)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', padding: 16, borderLeft: '3px solid #f59e0b' }}>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>{isRTL ? 'متوسط الحل' : 'Avg Resolution'}</div>
                <div style={{ color: '#f9fafb', fontSize: 28, fontWeight: 700 }}>{woStats.avg_resolution_hours != null ? woStats.avg_resolution_hours + 'h' : '-'}</div>
              </div>
              <div style={{ backgroundColor: 'rgba(26,32,56,0.8)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', padding: 16, borderLeft: '3px solid #8b5cf6' }}>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>{isRTL ? 'التزام SLA' : 'SLA Compliance'}</div>
                <div style={{ color: '#f9fafb', fontSize: 28, fontWeight: 700 }}>{woStats.sla_resolution_compliance != null ? woStats.sla_resolution_compliance + '%' : '-'}</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* By Priority */}
              {woStats.by_priority && woStats.by_priority.length > 0 && (
                <div style={{ backgroundColor: 'rgba(26,32,56,0.8)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', padding: 20 }}>
                  <h3 style={{ color: '#f9fafb', fontSize: 14, fontWeight: 600, marginBottom: 14 }}>{isRTL ? 'حسب الأولوية' : 'By Priority'}</h3>
                  {woStats.by_priority.map((p, i) => {
                    const colors = { critical: '#dc2626', high: '#f97316', medium: '#eab308', low: '#22c55e' };
                    const maxCnt = Math.max(...woStats.by_priority.map(x => x.cnt));
                    return (
                      <div key={i} style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ color: '#d1d5db', fontSize: 13, textTransform: 'capitalize' }}>{p.priority}</span>
                          <span style={{ color: '#f9fafb', fontSize: 13, fontWeight: 600 }}>{p.cnt}</span>
                        </div>
                        <div style={{ height: 6, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 3 }}>
                          <div style={{ width: maxCnt > 0 ? (p.cnt / maxCnt * 100) + '%' : '0%', height: '100%', borderRadius: 3, backgroundColor: colors[p.priority] || '#6b7280' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Technician Utilization */}
              {woStats.by_technician && woStats.by_technician.length > 0 && (
                <div style={{ backgroundColor: 'rgba(26,32,56,0.8)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', padding: 20 }}>
                  <h3 style={{ color: '#f9fafb', fontSize: 14, fontWeight: 600, marginBottom: 14 }}>{isRTL ? 'حمل الفنيين' : 'Technician Load'}</h3>
                  {woStats.by_technician.map((tech, i) => {
                    const maxCnt = Math.max(...woStats.by_technician.map(x => x.cnt));
                    return (
                      <div key={i} style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ color: '#d1d5db', fontSize: 13 }}>{tech.full_name}</span>
                          <span style={{ color: '#f9fafb', fontSize: 13, fontWeight: 600 }}>{tech.cnt} {isRTL ? 'أمر' : 'WOs'}</span>
                        </div>
                        <div style={{ height: 6, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 3 }}>
                          <div style={{ width: maxCnt > 0 ? (tech.cnt / maxCnt * 100) + '%' : '0%', height: '100%', borderRadius: 3, backgroundColor: '#667eea' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Costs this month */}
            {woStats.costs_this_month && (woStats.costs_this_month.labor > 0 || woStats.costs_this_month.parts > 0) && (
              <div style={{ marginTop: 16, backgroundColor: 'rgba(26,32,56,0.8)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', padding: 20, display: 'flex', gap: 30, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>{isRTL ? 'تكلفة العمل' : 'Labor Cost'}</div>
                  <div style={{ color: '#f9fafb', fontSize: 20, fontWeight: 700 }}>{Number(woStats.costs_this_month.labor || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>{isRTL ? 'تكلفة القطع' : 'Parts Cost'}</div>
                  <div style={{ color: '#f9fafb', fontSize: 20, fontWeight: 700 }}>{Number(woStats.costs_this_month.parts || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>{isRTL ? 'الإجمالي' : 'Total'}</div>
                  <div style={{ color: '#667eea', fontSize: 20, fontWeight: 700 }}>{Number(woStats.costs_this_month.total || 0).toLocaleString()}</div>
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </Layout>
  );
};

export default FSMDashboard;
